import React, {
  createContext,
  useReducer,
  useCallback,
  ReactNode,
  Dispatch,
  useMemo,
} from "react";
import { filterTabs } from "../../AppViewUtils";
import { FilterObject } from "../../FilterManagement/CommonFilterTypes";
import useUsedFields from "../../useUsedFields";
import {
  LS_FILTER,
  LS_REMEDIATION_ITEMS_FILTER,
} from "../../../Findings/FindingsFilters/FilterUtils";
import { filterTypes } from "../../../Findings/FindingsUtils";
import useSavedFilterType from "../useSavedFilterType";
import type { FilterEntity } from "../filterEntity";

export interface FiltersState {
  filtersList: FilterObject[];
  selectedFilter?: FilterObject;
  defaultFilter?: FilterObject;
  filterToExecute?: string;
  pendingFilterToExecute?: string | null;
  isEditFilter: boolean;
  isDirty: boolean;
  filtersMenuTab: string;
  shouldBlinkField?: string;
  shouldOpenField?: string;
  shouldOpenFiltersMenu?: boolean;
  shouldLoad?: boolean;
}

export interface FiltersContextType {
  filtersState: FiltersState;
  updateFiltersState: Dispatch<Partial<FiltersState>>;
  getFilterValueById: (
    filterId: string,
    filtersList?: FilterObject[]
  ) => FilterObject | undefined;
  usedFields: Set<string>;
  addUsedField: (field: string) => void;
  removeUsedField: (field: string) => void;
  lsFilterKey: string;
  filtersEntity?: FilterEntity;
}

export const filtersInitialValues: FiltersState = {
  filtersList: [],
  selectedFilter: undefined,
  defaultFilter: undefined,
  filterToExecute: undefined,
  pendingFilterToExecute: undefined,
  isEditFilter: false,
  isDirty: false,
  filtersMenuTab: filterTabs.RECOMMENDED,
  shouldBlinkField: undefined,
  shouldOpenField: undefined,
  shouldOpenFiltersMenu: false,
  shouldLoad: false,
};

export const FiltersContext = createContext<FiltersContextType>({
  filtersState: filtersInitialValues,
  updateFiltersState: () => {},
  getFilterValueById: () => undefined,
  usedFields: new Set(),
  addUsedField: () => {},
  removeUsedField: () => {},
  lsFilterKey: "",
});

interface FilterProviderProps {
  children: ReactNode;
  lsFilterKey?: string;
  filtersEntity?: FilterEntity;
}

const FilterProvider: React.FC<FilterProviderProps> = ({
  children,
  lsFilterKey: lsFilterKeyProp,
  filtersEntity,
}) => {
  const savedFilterType = useSavedFilterType(filtersEntity);
  const lsFilterKey =
    lsFilterKeyProp ??
    (savedFilterType === filterTypes.REMEDIATION_ITEM_FILTER
      ? LS_REMEDIATION_ITEMS_FILTER
      : LS_FILTER);

  const [filtersState, updateFiltersState] = useReducer(
    (state: FiltersState, newState: Partial<FiltersState>) => ({
      ...state,
      ...newState,
    }),
    filtersInitialValues
  );

  const { usedFields, addUsedField, removeUsedField } = useUsedFields();

  const getFilterValueById = useCallback(
    (
      filterId: string,
      filtersList: FilterObject[] = filtersState?.filtersList
    ): FilterObject | undefined => {
      return filtersList?.find((filter) => filter.value?.id === filterId);
    },
    [filtersState?.filtersList]
  );

  const filtersContextValue = useMemo(
    () => ({
      filtersState,
      updateFiltersState,
      getFilterValueById,
      usedFields,
      addUsedField,
      removeUsedField,
      lsFilterKey,
      filtersEntity,
    }),
    [
      filtersState,
      getFilterValueById,
      usedFields,
      addUsedField,
      removeUsedField,
      lsFilterKey,
      filtersEntity,
    ]
  );

  return (
    <FiltersContext.Provider value={filtersContextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export default FilterProvider;
